import type { QueryClient } from '@tanstack/react-query';
import type { PostHog } from 'posthog-js';
import type { LoaderFunctionArgs, ShouldRevalidateFunctionArgs } from 'react-router';

import { redirect } from 'react-router';

import { savedViews } from '#lib/react-query/queries.ts';
import { redeemMagicToken } from '#services/api/auth.ts';
import { requireAuthorizedInsightsUser } from '#utils/auth.ts';
import { setAccessToken } from '#utils/cookies.ts';
import {
  getParsedSearchParams,
  getReadinessMagicLinkSearchParams,
  getReadinessMagicLinkSearchParamsFromNextUrl,
  getSearchParamsToParse,
} from './Home.utils.ts';

export const loader =
  (queryClient: QueryClient, posthog: PostHog) => async (args: LoaderFunctionArgs) => {
    const url = new URL(args.request.url);
    await handleMiaMagicLink(queryClient, url);

    const me = await requireAuthorizedInsightsUser(queryClient, posthog);
    const savedViewsData = await queryClient.ensureQueryData(savedViews);
    const searchParams = url.searchParams;
    const viewId = searchParams.get('view');
    const activeView = savedViewsData.views.find((view) => view.id === Number(viewId));
    const paramsToParse = getSearchParamsToParse(searchParams, savedViewsData);

    return { ...getParsedSearchParams(paramsToParse), savedViewsData, activeView, me };
  };

export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  // Remove trailing slashes to handle cases where the URL might have them
  const pathname = args.currentUrl.pathname.replace(/\/+$/, '');

  // Don't revalidate when drilldown routes are rendered, as they change search
  // params that are shared with the home route.
  if (pathname === '/appointments/drilldown' || pathname === '/forms/drilldown') {
    return false;
  }

  return args.defaultShouldRevalidate;
}

async function handleMiaMagicLink(queryClient: QueryClient, url: URL) {
  const magicToken = url.searchParams.get('mt');

  if (!magicToken) {
    return;
  }

  // BAD-433: Legacy Mia v0 links are Home-route-specific because Coordinate is a Home tab.
  // Move this to route-agnostic auth bootstrap when another magic-link flow exists.
  const result = await redeemMagicToken(magicToken);

  if (result.status === 'failed') {
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect('/unauthorized?reason=expired-link');
  }

  if (result.status === 'redeemed') {
    setAccessToken(result.jwt);
    queryClient.clear();

    const redirectSearchParams = getReadinessMagicLinkSearchParamsFromNextUrl({
      nextUrl: result.nextUrl,
      fallbackSearchParams: url.searchParams,
    });

    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect(`${url.pathname}?${redirectSearchParams.toString()}`);
  }

  url.search = getReadinessMagicLinkSearchParams(url.searchParams).toString();

  // oxlint-disable-next-line typescript/only-throw-error
  throw redirect(`${url.pathname}${url.search}`);
}
